import React, { useEffect, useMemo, useRef, useState } from "react";
import GoogleMapReact from "google-map-react";
import { mapSecretkey } from "../../../config/map/mapData";
import { useNavigate } from "react-router-dom";

interface JobGroup {
    lat: number;
    lng: number;
    jobs: any[];
}

// Jobs whose coordinates match to 5 decimal places (~1m) share one marker
const groupJobsByLocation = (jobList: any[]): JobGroup[] => {
    const groups = new Map<string, JobGroup>();
    (jobList ?? []).forEach((job: any) => {
        const lat = Number(job?.location?.latitude);
        const lng = Number(job?.location?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            return;
        }
        const key = `${lat.toFixed(5)},${lng.toFixed(5)}`;
        if (!groups.has(key)) {
            groups.set(key, { lat, lng, jobs: [] });
        }
        groups.get(key)!.jobs.push(job);
    });
    return Array.from(groups.values());
};

const Markers = (props: any) => {
    const { jobs } = props;
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const closeTimer = useRef<number | undefined>(undefined);
    const listRef = useRef<HTMLDivElement>(null);
    const count = jobs.length;

    // Use real DOM mouse events rather than google-map-react's $hover: $hover is computed from
    // the wrapper's position, which is wrong once Google's fullscreen mode is on.
    // The short close delay lets the mouse cross the gap between the pin and the tooltip.
    const open = () => {
        window.clearTimeout(closeTimer.current);
        setIsOpen(true);
    };
    const scheduleClose = () => {
        window.clearTimeout(closeTimer.current);
        closeTimer.current = window.setTimeout(() => setIsOpen(false), 150);
    };
    useEffect(() => () => window.clearTimeout(closeTimer.current), []);

    // Stop wheel events reaching the map natively, so scrolling the list doesn't zoom the map
    useEffect(() => {
        const el = listRef.current;
        const stop = (e: WheelEvent) => e.stopPropagation();
        el?.addEventListener("wheel", stop, { passive: true });
        return () => el?.removeEventListener("wheel", stop);
    }, [isOpen]);

    return (
        <>
            <div className="d-flex justify-content-center" onMouseEnter={open} onMouseLeave={scheduleClose}>
                <div
                    role="button"
                    className="pin bounce"
                    title={count > 1 ? `${count} jobs` : jobs[0].title}
                    onClick={open}
                />
                {count > 1 && <div className="pin-count" onClick={open}>{count}</div>}
                <div className="pulse" >
                </div>
                {isOpen ?
                    <div className="location-tool-tip">
                        <div className="d-flex justify-content-between p-1">
                            <div className="location-text">
                                {jobs[0]?.location?.location}
                                {count > 1 && ` · ${count} jobs`}
                            </div>
                            <div className="text-end">
                                <i className="pi pi-star icon-star"></i>
                            </div>
                        </div>
                        <div ref={listRef} className="location-tool-tip-list">
                            {jobs.map((job: any) => (
                                <div key={job.id} className="location-tool-tip-item">
                                    <h4 role="button" className="mb-0 title" onClick={() =>
                                        navigate(`/view/${job.id}`, { state: "jobView" })
                                    }>{job.title}</h4>
                                    <div className="location-para overflow-para" dangerouslySetInnerHTML={{ __html: job.description }}></div>
                                </div>
                            ))}
                        </div>
                    </div>
                    :
                    ""
                }
            </div >
        </>
    );
};

const JobMapList = (props: any) => {
    const { jobList } = props;
    const groups = useMemo(() => groupJobsByLocation(jobList), [jobList]);

    if (!groups.length) {
        return (
            <div className="d-flex justify-content-center align-items-center p-5">
                No job locations to show on the map.
            </div>
        );
    }

    return (
        <>
            <div className="" style={{ height: "100vh", width: "100%" }}>
                <GoogleMapReact
                    bootstrapURLKeys={{ key: mapSecretkey }}
                    defaultCenter={{ lat: groups[0].lat, lng: groups[0].lng }}
                    defaultZoom={10}
                >
                    {groups.map((group) => (
                        <Markers
                            key={`${group.lat},${group.lng}`}
                            lat={group.lat}
                            lng={group.lng}
                            jobs={group.jobs}
                        />
                    ))}
                </GoogleMapReact>
            </div>
        </>
    );
};

export default JobMapList;

